/** Motion contract shared by React, Lit and CSS consumers. No application state. */
export const STREAMLAIN_MOTION = {
  enter: { duration: 200, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
  exit: { duration: 160, easing: 'cubic-bezier(0.4, 0, 1, 1)' },
  resize: { duration: 200, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
  feedback: { duration: 120, easing: 'ease-out' },
  /** Period change in a calendar: the new days slide in from the travel side. */
  slide: { duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
  /** Full-surface page cover: long, decelerating settle instead of a snap. */
  page: { duration: 560, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
  /** Page reveal on Back: same curve family, slightly quicker hand-back. */
  pageExit: { duration: 440, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' },
  /** Carousel settle after buttons, keys or a released drag (inertial feel). */
  glide: { duration: 620, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  /** A surface growing out of its small form and back, e.g. the canvas overview (ease-out cubic). */
  morph: { duration: 360, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' },
  docLoad: { duration: 250, easing: 'ease-in-out' },
  canvasEnter: { duration: 160, easing: 'ease-out' },
  overlay: { scale: 0.96, offset: 6 },
} as const;

export type StreamLainMotionKind = Exclude<
  keyof typeof STREAMLAIN_MOTION,
  'overlay'
>;
export type StreamLainMotionOutcome = 'finished' | 'cancelled';
export interface StreamLainAnimationHandle {
  readonly finished: Promise<StreamLainMotionOutcome>;
  cancel(): void;
}

export function streamLainMotionTiming(kind: StreamLainMotionKind): string {
  const { duration, easing } = STREAMLAIN_MOTION[kind];
  return `${duration}ms ${easing} both`;
}

export function streamLainOverlayKeyframes(
  direction: 'bottom' | 'left' | 'right' = 'bottom',
  phase: 'enter' | 'exit' = 'enter'
) {
  const axis = direction === 'bottom' ? 'Y' : 'X';
  const offset =
    direction === 'right'
      ? -STREAMLAIN_MOTION.overlay.offset
      : STREAMLAIN_MOTION.overlay.offset;
  const resting = { opacity: 1, transform: `translate${axis}(0) scale(1)` };
  const displaced = {
    opacity: 0,
    transform: `translate${axis}(${offset}px) scale(${STREAMLAIN_MOTION.overlay.scale})`,
  };
  return phase === 'enter'
    ? { from: displaced, to: resting }
    : { from: resting, to: displaced };
}

const CSS_EASING_CURVES: Record<string, [number, number, number, number]> = {
  linear: [0, 0, 1, 1],
  ease: [0.25, 0.1, 0.25, 1],
  'ease-in': [0.42, 0, 1, 1],
  'ease-out': [0, 0, 0.58, 1],
  'ease-in-out': [0.42, 0, 0.58, 1],
};

/** JS progress curve for a motion easing, so scroll motion matches CSS motion. */
export function streamLainMotionProgress(
  kind: StreamLainMotionKind
): (t: number) => number {
  const { easing } = STREAMLAIN_MOTION[kind];
  const match = /^cubic-bezier\(([^)]+)\)$/.exec(easing);
  const curve = match
    ? (match[1].split(',').map(Number) as [number, number, number, number])
    : CSS_EASING_CURVES[easing];
  if (!curve || curve.some(value => !Number.isFinite(value))) {
    throw new Error(`Unsupported motion easing: ${easing}`);
  }
  const [x1, y1, x2, y2] = curve;
  const bezier = (t: number, a: number, b: number) =>
    3 * a * t * (1 - t) ** 2 + 3 * b * t ** 2 * (1 - t) + t ** 3;
  const slope = (t: number, a: number, b: number) =>
    3 * a * (1 - t) ** 2 + 6 * (b - a) * t * (1 - t) + 3 * (1 - b) * t ** 2;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const error = bezier(t, x1, x2) - x;
      const d = slope(t, x1, x2);
      if (Math.abs(error) < 1e-6 || Math.abs(d) < 1e-6) break;
      t -= error / d;
    }
    if (t < 0 || t > 1 || Math.abs(bezier(t, x1, x2) - x) > 1e-4) {
      let low = 0;
      let high = 1;
      t = x;
      for (let i = 0; i < 30; i++) {
        if (bezier(t, x1, x2) < x) low = t;
        else high = t;
        t = (low + high) / 2;
      }
    }
    return bezier(t, y1, y2);
  };
}
